class MVAnimeDerby {
  constructor(root) {
    this.root = root;
    this.canvas = root.querySelector('[data-derby-canvas]');
    this.ctx = this.canvas.getContext('2d');
    this.startButtons = root.querySelectorAll('[data-start-game]');
    this.restartButton = root.querySelector('[data-restart-game]');
    this.swingButton = root.querySelector('[data-swing]');
    this.form = root.querySelector('[data-score-form]');
    this.formStatus = root.querySelector('[data-form-status]');
    this.leaderboardList = root.querySelector('[data-leaderboard-list]');
    this.timeLeftEl = root.querySelector('[data-time-left]');
    this.scoreEl = root.querySelector('[data-score]');
    this.homeRunsEl = root.querySelector('[data-home-runs]');
    this.longestEl = root.querySelector('[data-longest]');
    this.countdownEl = root.querySelector('[data-countdown]');
    this.finalScoreEl = root.querySelector('[data-final-score]');
    this.finalHomeRunsEl = root.querySelector('[data-final-home-runs]');
    this.finalLongestEl = root.querySelector('[data-final-longest]');
    this.finalPerfectsEl = root.querySelector('[data-final-perfects]');
    this.finalBestComboEl = root.querySelector('[data-final-best-combo]');
    this.messageEyebrowEl = root.querySelector('[data-hud-eyebrow]');
    this.personalBestEls = root.querySelectorAll('[data-personal-best]');
    this.unlockScoreEls = root.querySelectorAll('[data-unlock-score]');
    this.rewardPanels = root.querySelectorAll('[data-reward-panel]');
    this.rewardCodeEls = root.querySelectorAll('[data-reward-code]');
    this.rewardCtaEls = root.querySelectorAll('[data-reward-cta]');
    this.rewardTitleEl = root.querySelector('[data-reward-title]');
    this.rewardBodyEl = root.querySelector('[data-reward-body]');

    this.leaderboardUrl = root.dataset.leaderboardUrl || '';
    this.submitUrl = root.dataset.submitUrl || '';
    this.roundSeconds = Number(root.dataset.gameSeconds || 45);
    this.unlockScore = Number(root.dataset.unlockScore || 900);
    this.rewardCode = String(root.dataset.rewardCode || '').trim();
    this.rewardTitle = String(root.dataset.rewardTitle || 'Unlocked');
    this.rewardBody = String(root.dataset.rewardBody || 'You cleared the score target.');
    this.rewardUrl = String(root.dataset.rewardUrl || '').trim();
    this.rewardLinkLabel = String(root.dataset.rewardLinkLabel || 'Shop now').trim() || 'Shop now';
    this.campaign = String(root.dataset.campaign || 'moonshot_protocol').trim();

    this.overlayIntro = root.querySelector('[data-overlay="intro"]');
    this.overlayCountdown = root.querySelector('[data-overlay="countdown"]');
    this.overlayEnd = root.querySelector('[data-overlay="end"]');

    this.resizeCanvas = this.resizeCanvas.bind(this);
    this.gameLoop = this.gameLoop.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    this.storageKey = 'mv-anime-derby-profile';
    this.dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
    this.world = { width: 1280, height: 720 };

    this.state = 'idle';
    this.lastTimestamp = 0;
    this.animationFrame = null;

    this.loadProfile();
    this.resetRound();
    this.bindEvents();
    this.resizeCanvas();
    this.applyRewardContent();
    this.fetchLeaderboard();
    this.gameLoop(0);
  }

  bindEvents() {
    this.startButtons.forEach((button) => {
      button.addEventListener('click', () => this.startCountdown());
    });

    if (this.restartButton) {
      this.restartButton.addEventListener('click', () => {
        this.hideOverlay(this.overlayEnd);
        this.showOverlay(this.overlayIntro);
        this.resetRound();
      });
    }

    if (this.swingButton) {
      this.swingButton.addEventListener('click', () => this.swing());
    }

    if (this.form) {
      this.form.addEventListener('submit', (event) => this.submitScore(event));
    }

    window.addEventListener('resize', this.resizeCanvas);
    window.addEventListener('keydown', this.handleKeydown);
  }

  handleKeydown(event) {
    if (event.code !== 'Space') return;
    const activeTag = document.activeElement?.tagName;
    if (activeTag === 'INPUT' || activeTag === 'TEXTAREA') return;
    event.preventDefault();
    if (this.state === 'playing') this.swing();
    if (this.state === 'idle') this.startCountdown();
  }

  resizeCanvas() {
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = Math.floor(rect.width * this.dpr);
    this.canvas.height = Math.floor(rect.height * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  loadProfile() {
    this.profile = { name: '', email: '', bestScore: 0 };

    try {
      const saved = JSON.parse(window.localStorage.getItem(this.storageKey) || '{}');
      this.profile.name = typeof saved.name === 'string' ? saved.name : '';
      this.profile.email = typeof saved.email === 'string' ? saved.email : '';
      this.profile.bestScore = Number(saved.bestScore || 0);
    } catch (error) {
      console.warn('Could not load derby profile', error);
    }

    this.updatePersonalBestUI();
  }

  saveProfile(payload = {}) {
    this.profile = {
      ...this.profile,
      ...payload,
      bestScore: Math.max(Number(this.profile.bestScore || 0), Number(payload.bestScore || 0)),
    };

    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(this.profile));
    } catch (error) {
      console.warn('Could not save derby profile', error);
    }

    this.updatePersonalBestUI();
  }

  updatePersonalBestUI() {
    this.personalBestEls.forEach((el) => {
      el.textContent = String(this.profile.bestScore || 0);
    });
  }

  applyRewardContent() {
    this.unlockScoreEls.forEach((el) => {
      el.textContent = this.unlockScore;
    });

    if (this.rewardTitleEl) this.rewardTitleEl.textContent = this.rewardTitle;
    if (this.rewardBodyEl) this.rewardBodyEl.textContent = this.rewardBody;

    this.rewardCodeEls.forEach((el) => {
      el.textContent = this.rewardCode || 'UNLOCKED';
      el.toggleAttribute('hidden', !this.rewardCode);
    });

    this.rewardCtaEls.forEach((el) => {
      if (!this.rewardUrl) {
        el.setAttribute('hidden', 'hidden');
        return;
      }
      el.removeAttribute('hidden');
      el.href = this.rewardUrl;
      el.textContent = this.rewardLinkLabel;
    });
  }

  setRewardUnlocked(unlocked) {
    this.rewardPanels.forEach((panel) => {
      panel.classList.toggle('is-unlocked', unlocked);
      panel.toggleAttribute('hidden', !unlocked);
    });
  }

  resetRound() {
    this.score = 0;
    this.homeRuns = 0;
    this.longest = 0;
    this.perfects = 0;
    this.combo = 0;
    this.bestCombo = 0;
    this.timeLeft = this.roundSeconds;
    this.pitchTimer = 0;
    this.messageTimer = 0;
    this.flashTimer = 0;
    this.screenShake = 0;
    this.countdownTime = 3;
    this.ballTrail = [];
    this.particles = [];
    this.floaters = [];
    this.scanlinesOffset = 0;
    this.skyPulse = 0;
    this.message = 'Wait on the pitch.';
    this.messageEyebrow = 'MOUNT VALOROUS // MOONSHOT PROTOCOL';
    this.pitchLabel = '';
    this.pitchCount = 0;
    this.batter = {
      x: 328,
      y: 528,
      swingTimer: 0,
      batAura: 0,
    };
    this.ball = null;
    this.submitted = false;
    this.roundPlayedMs = 0;
    this.pendingResult = {
      score: 0,
      home_runs: 0,
      longest_ft: 0,
      perfect_swings: 0,
      best_combo: 0,
      reward_unlocked: false,
      played_ms: this.roundSeconds * 1000,
      campaign: this.campaign,
    };

    if (this.form) {
      this.form.reset();
      const nameInput = this.form.querySelector('input[name="player_name"]');
      const emailInput = this.form.querySelector('input[name="email"]');
      const optInInput = this.form.querySelector('input[name="marketing_opt_in"]');
      if (nameInput && this.profile.name) nameInput.value = this.profile.name;
      if (emailInput && this.profile.email) emailInput.value = this.profile.email;
      if (optInInput) optInInput.checked = true;
    }

    this.setRewardUnlocked(false);
    this.updateStats();
    if (this.formStatus) this.formStatus.textContent = '';
    if (this.swingButton) this.swingButton.disabled = true;
  }

  updateStats() {
    this.timeLeftEl.textContent = Math.max(0, Math.ceil(this.timeLeft));
    this.scoreEl.textContent = this.score;
    this.homeRunsEl.textContent = this.homeRuns;
    this.longestEl.textContent = this.longest;
  }

  startCountdown() {
    if (this.state === 'countdown' || this.state === 'playing') return;
    this.resetRound();
    this.state = 'countdown';
    this.countdownTime = 3;
    this.hideOverlay(this.overlayIntro);
    this.hideOverlay(this.overlayEnd);
    this.showOverlay(this.overlayCountdown);
    this.countdownEl.textContent = this.countdownTime;
  }

  startRound() {
    this.state = 'playing';
    this.hideOverlay(this.overlayCountdown);
    if (this.swingButton) this.swingButton.disabled = false;
    this.message = 'Perfect swings create the biggest blast.';
    this.messageEyebrow = 'ENTER THE CAGE';
    this.pitchTimer = 0.55;
    this.roundPlayedMs = 0;
  }

  endRound() {
    this.state = 'ended';
    if (this.swingButton) this.swingButton.disabled = true;
    this.ball = null;

    const rewardUnlocked = this.score >= this.unlockScore;

    this.pendingResult = {
      score: this.score,
      home_runs: this.homeRuns,
      longest_ft: this.longest,
      perfect_swings: this.perfects,
      best_combo: this.bestCombo,
      reward_unlocked: rewardUnlocked,
      played_ms: Math.round(this.roundPlayedMs),
      campaign: this.campaign,
    };

    this.finalScoreEl.textContent = this.score;
    this.finalHomeRunsEl.textContent = this.homeRuns;
    this.finalLongestEl.textContent = this.longest;
    this.finalPerfectsEl.textContent = this.perfects;
    if (this.finalBestComboEl) this.finalBestComboEl.textContent = this.bestCombo;

    this.setRewardUnlocked(rewardUnlocked);
    this.showOverlay(this.overlayEnd);
  }

  showOverlay(overlay) {
    if (overlay) overlay.classList.add('is-visible');
  }

  hideOverlay(overlay) {
    if (overlay) overlay.classList.remove('is-visible');
  }

  swing() {
    if (this.state !== 'playing') return;
    this.batter.swingTimer = 0.18;
    this.batter.batAura = 0.2;

    if (!this.ball || this.ball.resolved) {
      this.onMiss('Late');
      return;
    }

    const sweetSpotX = this.batter.x + 156;
    const sweetSpotY = this.batter.y - 98;
    const dx = Math.abs(this.ball.x - sweetSpotX);
    const dy = Math.abs(this.ball.y - sweetSpotY);

    if (dx > 84 || dy > 76) {
      this.onMiss(dx < 104 ? 'Just off' : 'Whiff');
      return;
    }

    const timingScore = Math.max(0, 1 - dx / 84);
    const verticalScore = Math.max(0, 1 - dy / 76);
    const contactScore = (timingScore * 0.72) + (verticalScore * 0.28);
    const launchFactor = 0.88 + (contactScore * 0.44);
    const pitchPower = this.ball.pitchClass === 'splitter'
      ? 0.94
      : this.ball.pitchClass === 'heater'
        ? 1.08
        : 1;

    const distance = Math.max(
      275,
      Math.round(295 + (contactScore * 195 * pitchPower * launchFactor) + (Math.random() * 18 - 9)),
    );
    const perfect = contactScore > 0.91;
    const homeRun = distance >= 360;
    const basePoints = homeRun ? 110 : 18;
    const perfectBonus = perfect ? 35 : 0;
    const distanceBonus = homeRun ? Math.max(0, Math.round((distance - 360) * 1.15)) : Math.round(distance * 0.06);
    const comboBonus = homeRun ? Math.min(this.combo * 8, 48) : 0;
    const gained = basePoints + perfectBonus + distanceBonus + comboBonus;
    const sfx = perfect ? 'KA-BOOM!' : homeRun ? 'KRAK!' : 'ZIP!';

    this.score += gained;
    this.longest = Math.max(this.longest, distance);

    if (homeRun) {
      this.homeRuns += 1;
      this.combo += 1;
      this.bestCombo = Math.max(this.bestCombo, this.combo);
      this.flashTimer = 0.18;
      this.screenShake = perfect ? 18 : 12;
      this.skyPulse = 0.28;
      this.spawnParticles(this.ball.x, this.ball.y, perfect ? 42 : 28, perfect ? 'perfect' : 'homer');
      this.addFloater(`${sfx} +${gained}`, this.ball.x, this.ball.y - 34, perfect ? 'perfect' : 'homer');
      this.addFloater(`${distance} FT`, this.ball.x + 40, this.ball.y - 74, 'distance');
      this.messageEyebrow = perfect ? 'ZENITH ZONE' : 'MOUNT VALOROUS POWER';
      this.message = perfect ? `Perfect blast — ${distance} ft` : `Moonshot launched — ${distance} ft`;
    } else {
      this.combo = 0;
      this.spawnParticles(this.ball.x, this.ball.y, 14, 'contact');
      this.addFloater(`${distance} FT`, this.ball.x, this.ball.y - 22, 'contact');
      this.messageEyebrow = 'TRACKED BALL';
      this.message = `On the track — ${distance} ft`;
    }

    if (perfect) this.perfects += 1;

    this.ball.resolved = true;
    this.ball.hit = true;
    this.ball.hitTimer = 0.44;
    this.ball.vx = 18 + (contactScore * 22);
    this.ball.vy = -(12 + (contactScore * 18));
    this.updateStats();
  }

  onMiss(label) {
    this.combo = 0;
    this.messageEyebrow = 'RESET';
    this.message = `${label}. Stay through the zone.`;
    if (this.ball && !this.ball.resolved) {
      this.ball.resolved = true;
      this.ball.hit = false;
    }
    this.addFloater(label.toUpperCase(), this.batter.x + 144, this.batter.y - 124, 'miss');
  }

  spawnPitch() {
    const pitchIndex = Math.floor(Math.random() * 3);
    const pitchMap = [
      { name: 'HEATER', pitchClass: 'heater', vx: -(11.3 + Math.random() * 0.5), vy: 1.2 + Math.random() * 0.5, curve: (Math.random() - 0.5) * 0.5 },
      { name: 'CUTTER', pitchClass: 'cutter', vx: -(10.2 + Math.random() * 0.7), vy: 1.8 + Math.random() * 0.5, curve: (Math.random() < 0.5 ? -1 : 1) * (0.9 + Math.random() * 0.35) },
      { name: 'SPLITTER', pitchClass: 'splitter', vx: -(10.1 + Math.random() * 0.5), vy: 2.3 + Math.random() * 0.5, curve: (Math.random() - 0.5) * 0.4 },
    ][pitchIndex];

    this.ball = {
      x: this.world.width - 110,
      y: 274 + Math.random() * 128,
      vx: pitchMap.vx,
      vy: pitchMap.vy,
      curve: pitchMap.curve,
      life: 0,
      resolved: false,
      hit: false,
      hitTimer: 0,
      pitchClass: pitchMap.pitchClass,
      pitchName: pitchMap.name,
      seamRotation: Math.random() * Math.PI * 2,
    };

    this.pitchCount += 1;
    this.ballTrail = [];
    this.messageEyebrow = `PITCH ${this.pitchCount.toString().padStart(2, '0')} // ${pitchMap.name}`;
    this.message = pitchMap.pitchClass === 'splitter'
      ? 'Stay back — it falls late.'
      : pitchMap.pitchClass === 'heater'
        ? 'Quick hands.'
        : 'Watch the late cut.';
  }

  spawnParticles(x, y, count, type = 'default') {
    const palettes = {
      homer: ['#8effd4', '#d8fff4', '#f4ff8b'],
      perfect: ['#f4ff8b', '#ffffff', '#8effd4'],
      contact: ['#ffffff', '#8effd4'],
      default: ['#8effd4', '#ffffff'],
    };
    const colors = palettes[type] || palettes.default;

    for (let index = 0; index < count; index += 1) {
      this.particles.push({
        x,
        y,
        vx: (Math.random() - 0.5) * 8,
        vy: (Math.random() - 0.5) * 8,
        life: 0.8 + Math.random() * 0.45,
        size: 2 + Math.random() * 5,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }
  }

  addFloater(text, x, y, type = 'default') {
    this.floaters.push({ text, x, y, type, life: 1.15 });
  }

  update(delta) {
    this.scanlinesOffset += delta * 52;
    this.skyPulse = Math.max(0, this.skyPulse - delta * 0.9);
    this.screenShake = Math.max(0, this.screenShake - delta * 54);
    this.batter.swingTimer = Math.max(0, this.batter.swingTimer - delta);
    this.batter.batAura = Math.max(0, this.batter.batAura - delta * 0.9);

    if (this.state === 'countdown') {
      this.countdownTime -= delta;
      const display = Math.ceil(this.countdownTime);
      this.countdownEl.textContent = display > 0 ? display : 'GO';
      if (this.countdownTime <= 0) this.startRound();
    }

    if (this.state === 'playing') {
      this.timeLeft -= delta;
      this.roundPlayedMs += delta * 1000;
      this.pitchTimer -= delta;

      if (!this.ball && this.pitchTimer <= 0) {
        this.spawnPitch();
        this.pitchTimer = 0.9 + Math.random() * 0.56;
      }

      if (this.ball) {
        this.ball.life += delta;
        this.ball.seamRotation += delta * 8;

        if (this.ball.hit) {
          this.ball.hitTimer -= delta;
          this.ball.x += this.ball.vx * 8 * delta;
          this.ball.y += this.ball.vy * 8 * delta;
          this.ball.vy += 26 * delta;
          if (this.ball.hitTimer <= 0 || this.ball.y > this.world.height + 50 || this.ball.x > this.world.width + 220) {
            this.ball = null;
          }
        } else {
          this.ball.x += this.ball.vx * 60 * delta;
          const splitterDrop = this.ball.pitchClass === 'splitter' ? Math.max(0, this.ball.life - 0.18) * 4.8 : 0;
          this.ball.y += this.ball.vy * 60 * delta + Math.sin(this.ball.life * 8) * this.ball.curve + splitterDrop;

          this.ballTrail.push({ x: this.ball.x, y: this.ball.y, life: 0.22, pitchClass: this.ball.pitchClass });
          if (this.ballTrail.length > 12) this.ballTrail.shift();

          if (this.ball.x < this.batter.x + 100 && !this.ball.resolved) {
            this.onMiss('Too late');
          }

          if (this.ball.x < this.batter.x - 70 || this.ball.resolved) {
            this.ball = null;
          }
        }
      }

      if (this.timeLeft <= 0) {
        this.timeLeft = 0;
        this.updateStats();
        this.endRound();
      }
    }

    this.ballTrail.forEach((trail) => {
      trail.life -= delta;
    });
    this.ballTrail = this.ballTrail.filter((trail) => trail.life > 0);

    this.particles.forEach((particle) => {
      particle.life -= delta;
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.vy += 0.22;
    });
    this.particles = this.particles.filter((particle) => particle.life > 0);

    this.floaters.forEach((floater) => {
      floater.life -= delta;
      floater.y -= 34 * delta;
    });
    this.floaters = this.floaters.filter((floater) => floater.life > 0);

    this.flashTimer = Math.max(0, this.flashTimer - delta);
    this.updateStats();
  }

  drawBackground() {
    const ctx = this.ctx;
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;

    ctx.save();

    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, '#050812');
    bg.addColorStop(0.42, '#101729');
    bg.addColorStop(1, '#05070d');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    const skyBurst = ctx.createRadialGradient(w * 0.72, h * 0.16, 20, w * 0.72, h * 0.16, w * 0.52);
    skyBurst.addColorStop(0, `rgba(143, 255, 214, ${0.22 + this.skyPulse * 0.4})`);
    skyBurst.addColorStop(0.35, 'rgba(130, 255, 214, 0.12)');
    skyBurst.addColorStop(1, 'rgba(130, 255, 214, 0)');
    ctx.fillStyle = skyBurst;
    ctx.fillRect(0, 0, w, h);

    ctx.fillStyle = 'rgba(255,255,255,0.05)';
    for (let x = -20; x < w + 30; x += 22) {
      for (let y = 14; y < h * 0.54; y += 18) {
        ctx.beginPath();
        ctx.arc(x + (((y / 18) % 2) * 7), y, 1.35, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    for (let index = 0; index < 10; index += 1) {
      ctx.strokeStyle = `rgba(142,255,212,${0.08 + (index * 0.006)})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(-40, h * (0.14 + index * 0.045));
      ctx.lineTo(w + 80, h * (0.01 + index * 0.074));
      ctx.stroke();
    }

    ctx.fillStyle = '#0b1121';
    ctx.fillRect(0, h * 0.76, w, h * 0.24);

    ctx.fillStyle = '#12213f';
    ctx.beginPath();
    ctx.moveTo(0, h * 0.74);
    ctx.quadraticCurveTo(w * 0.2, h * 0.55, w * 0.5, h * 0.62);
    ctx.quadraticCurveTo(w * 0.78, h * 0.53, w, h * 0.72);
    ctx.lineTo(w, h);
    ctx.lineTo(0, h);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#08111f';
    ctx.beginPath();
    ctx.arc(w * 0.5, h * 1.2, w * 0.82, Math.PI, Math.PI * 2, false);
    ctx.fill();

    const crowdBandY = h * 0.575;
    ctx.fillStyle = '#0b1223';
    ctx.fillRect(0, crowdBandY, w, h * 0.15);
    for (let x = 0; x < w + 12; x += 18) {
      const height = 12 + ((x / 18) % 4) * 8;
      ctx.fillStyle = x % 36 === 0 ? 'rgba(142,255,212,0.18)' : 'rgba(255,255,255,0.08)';
      ctx.fillRect(x, crowdBandY + 42 - height, 10, height);
    }

    ctx.strokeStyle = 'rgba(255,255,255,0.95)';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(w * 0.19, h * 0.82);
    ctx.lineTo(w * 0.52, h * 0.63);
    ctx.lineTo(w * 0.81, h * 0.8);
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(w * 0.49, h * 0.69);
    ctx.lineTo(w * 0.52, h * 0.63);
    ctx.lineTo(w * 0.55, h * 0.69);
    ctx.lineTo(w * 0.52, h * 0.75);
    ctx.closePath();
    ctx.fill();

    const moon = ctx.createRadialGradient(w * 0.78, h * 0.16, 12, w * 0.78, h * 0.16, 62);
    moon.addColorStop(0, 'rgba(255,255,255,0.98)');
    moon.addColorStop(0.18, 'rgba(248,255,138,0.96)');
    moon.addColorStop(1, 'rgba(248,255,138,0)');
    ctx.fillStyle = moon;
    ctx.beginPath();
    ctx.arc(w * 0.78, h * 0.16, 62, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(6,10,17,0.32)';
    for (let y = -4; y < h + 6; y += 5) {
      ctx.fillRect(0, y + (this.scanlinesOffset % 5), w, 1);
    }

    if (this.flashTimer > 0) {
      ctx.fillStyle = `rgba(142,255,212,${this.flashTimer * 0.34})`;
      ctx.fillRect(0, 0, w, h);
    }

    ctx.restore();
  }

  drawBatter() {
    const ctx = this.ctx;
    const scaleX = this.canvas.clientWidth / this.world.width;
    const scaleY = this.canvas.clientHeight / this.world.height;
    const x = this.batter.x * scaleX;
    const y = this.batter.y * scaleY;
    const swing = this.batter.swingTimer > 0;

    ctx.save();
    if (this.screenShake > 0) {
      ctx.translate((Math.random() - 0.5) * this.screenShake, (Math.random() - 0.5) * this.screenShake);
    }
    ctx.translate(x, y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (this.batter.batAura > 0) {
      ctx.strokeStyle = `rgba(142,255,212,${this.batter.batAura * 0.8})`;
      ctx.lineWidth = 28;
      ctx.beginPath();
      ctx.moveTo(44, swing ? -84 : -42);
      ctx.lineTo(swing ? 168 : 144, swing ? -150 : -98);
      ctx.stroke();
    }

    ctx.fillStyle = '#071017';
    ctx.strokeStyle = '#071017';
    ctx.lineWidth = 16;

    ctx.beginPath();
    ctx.arc(0, -116, 28, 0, Math.PI * 2);
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(-28, -88);
    ctx.lineTo(0, -66);
    ctx.lineTo(30, -86);
    ctx.lineTo(26, 0);
    ctx.lineTo(-20, 8);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#eef7ff';
    ctx.lineWidth = 16;
    ctx.beginPath();
    ctx.moveTo(-2, -80);
    ctx.lineTo(0, 18);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(0, -46);
    ctx.lineTo(swing ? 78 : 58, swing ? -88 : -42);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(-2, -46);
    ctx.lineTo(-56, 18);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(0, 18);
    ctx.lineTo(-36, 110);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(0, 18);
    ctx.lineTo(46, 108);
    ctx.stroke();

    ctx.strokeStyle = '#8effd4';
    ctx.lineWidth = 10;
    ctx.beginPath();
    ctx.moveTo(46, swing ? -86 : -42);
    ctx.lineTo(swing ? 160 : 140, swing ? -144 : -98);
    ctx.stroke();

    ctx.restore();
  }

  drawBall() {
    if (!this.ball) return;

    const ctx = this.ctx;
    const scaleX = this.canvas.clientWidth / this.world.width;
    const scaleY = this.canvas.clientHeight / this.world.height;

    this.ballTrail.forEach((trail) => {
      const color = trail.pitchClass === 'splitter'
        ? 'rgba(248,255,138,'
        : trail.pitchClass === 'heater'
          ? 'rgba(142,255,212,'
          : 'rgba(173,216,255,';
      ctx.fillStyle = `${color}${Math.max(0, trail.life * 2.5)})`;
      ctx.beginPath();
      ctx.arc(trail.x * scaleX, trail.y * scaleY, 8 * trail.life, 0, Math.PI * 2);
      ctx.fill();
    });

    const x = this.ball.x * scaleX;
    const y = this.ball.y * scaleY;
    const glow = ctx.createRadialGradient(x, y, 3, x, y, 22);
    glow.addColorStop(0, 'rgba(255,255,255,1)');
    glow.addColorStop(0.35, this.ball.pitchClass === 'splitter' ? 'rgba(248,255,138,0.96)' : this.ball.pitchClass === 'heater' ? 'rgba(142,255,212,0.95)' : 'rgba(173,216,255,0.92)');
    glow.addColorStop(1, 'rgba(142,255,212,0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(x, y, 20, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(x, y, 7, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = 'rgba(255,80,80,0.8)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(x - 2, y, 4.5, Math.PI * 0.15 + this.ball.seamRotation, Math.PI * 0.9 + this.ball.seamRotation);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(x + 2, y, 4.5, Math.PI * 1.15 + this.ball.seamRotation, Math.PI * 1.9 + this.ball.seamRotation);
    ctx.stroke();
  }

  drawParticlesAndFloaters() {
    const ctx = this.ctx;
    const scaleX = this.canvas.clientWidth / this.world.width;
    const scaleY = this.canvas.clientHeight / this.world.height;

    this.particles.forEach((particle) => {
      const alpha = Math.max(0, particle.life);
      ctx.fillStyle = this.hexToRgba(particle.color, alpha);
      ctx.beginPath();
      ctx.arc(particle.x * scaleX, particle.y * scaleY, particle.size, 0, Math.PI * 2);
      ctx.fill();
    });

    this.floaters.forEach((floater) => {
      const colors = {
        homer: '#8effd4',
        perfect: '#f4ff8b',
        miss: '#ff6f8b',
        contact: '#ffffff',
        distance: '#9dd8ff',
        default: '#ffffff',
      };
      ctx.fillStyle = colors[floater.type] || colors.default;
      ctx.font = `900 ${floater.type === 'perfect' ? 28 : floater.type === 'homer' ? 24 : 20}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(floater.text, floater.x * scaleX, floater.y * scaleY);
    });
  }

  drawHud() {
    const ctx = this.ctx;
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;

    ctx.fillStyle = 'rgba(6, 10, 17, 0.72)';
    ctx.fillRect(22, 18, 388, 92);
    ctx.strokeStyle = 'rgba(142,255,212,0.5)';
    ctx.lineWidth = 2;
    ctx.strokeRect(22, 18, 388, 92);

    ctx.fillStyle = '#8effd4';
    ctx.font = '800 13px system-ui, sans-serif';
    ctx.fillText(this.messageEyebrow, 40, 44);
    ctx.fillStyle = '#ffffff';
    ctx.font = '900 27px system-ui, sans-serif';
    ctx.fillText(this.message, 40, 80);

    ctx.fillStyle = 'rgba(6, 10, 17, 0.72)';
    ctx.fillRect(w - 228, 18, 206, 92);
    ctx.strokeStyle = 'rgba(244,255,139,0.45)';
    ctx.strokeRect(w - 228, 18, 206, 92);
    ctx.fillStyle = '#f4ff8b';
    ctx.font = '800 12px system-ui, sans-serif';
    ctx.fillText('BEST STREAK', w - 206, 44);
    ctx.fillStyle = '#ffffff';
    ctx.font = '900 34px system-ui, sans-serif';
    ctx.fillText(String(this.bestCombo), w - 206, 82);

    if (this.state === 'playing') {
      const barX = 24;
      const barY = h - 38;
      const barW = w - 48;
      const progress = this.timeLeft / this.roundSeconds;
      ctx.fillStyle = 'rgba(255,255,255,0.08)';
      ctx.fillRect(barX, barY, barW, 12);
      const progressFill = ctx.createLinearGradient(barX, barY, barX + barW, barY);
      progressFill.addColorStop(0, '#8effd4');
      progressFill.addColorStop(1, '#f4ff8b');
      ctx.fillStyle = progressFill;
      ctx.fillRect(barX, barY, barW * progress, 12);
    }
  }

  render() {
    this.drawBackground();
    this.drawBatter();
    this.drawBall();
    this.drawParticlesAndFloaters();
    this.drawHud();
  }

  gameLoop(timestamp) {
    const delta = Math.min((timestamp - this.lastTimestamp) / 1000 || 0, 0.033);
    this.lastTimestamp = timestamp;
    this.update(delta);
    this.render();
    this.animationFrame = window.requestAnimationFrame(this.gameLoop);
  }

  async fetchLeaderboard() {
    if (!this.leaderboardUrl) {
      this.leaderboardList.innerHTML = '<li class="mv-derby-leaderboard-empty">Add your leaderboard endpoint URL in the section settings.</li>';
      return;
    }

    try {
      const leaderboardRequestUrl = new URL(this.leaderboardUrl, window.location.origin);
      leaderboardRequestUrl.searchParams.set('campaign', this.campaign);
      leaderboardRequestUrl.searchParams.set('limit', '10');
      const response = await fetch(leaderboardRequestUrl.toString(), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Leaderboard request failed: ${response.status}`);
      const payload = await response.json();
      const entries = Array.isArray(payload.entries) ? payload.entries : [];

      if (!entries.length) {
        this.leaderboardList.innerHTML = '<li class="mv-derby-leaderboard-empty">No scores yet. Be the first player up.</li>';
        return;
      }

      this.leaderboardList.innerHTML = entries.map((entry, index) => {
        const safeName = this.escapeHtml(entry.player_name || 'Player');
        const score = Number(entry.score || 0);
        const homeRuns = Number(entry.home_runs || 0);
        const longest = Number(entry.longest_ft || 0);
        const perfects = Number(entry.perfect_swings || 0);
        const badge = index === 0 ? 'CROWN' : index === 1 ? 'ELITE' : index === 2 ? 'HOT' : 'TOP';
        return `
          <li class="mv-derby-leaderboard-row ${index < 3 ? 'is-podium' : ''}">
            <span class="mv-derby-rank">#${index + 1}</span>
            <div class="mv-derby-player-meta">
              <div class="mv-derby-player-topline">
                <strong>${safeName}</strong>
                <span class="mv-derby-badge">${badge}</span>
              </div>
              <span>${homeRuns} HR · ${longest} ft · ${perfects} perfect</span>
            </div>
            <span class="mv-derby-player-score">${score}</span>
          </li>
        `;
      }).join('');
    } catch (error) {
      console.error(error);
      this.leaderboardList.innerHTML = '<li class="mv-derby-leaderboard-empty">Could not load the leaderboard right now.</li>';
    }
  }

  async submitScore(event) {
    event.preventDefault();
    if (this.submitted) return;

    if (!this.submitUrl) {
      this.formStatus.textContent = 'Add your score submit endpoint URL in the section settings first.';
      return;
    }

    const formData = new FormData(this.form);
    const honeypot = String(formData.get('website') || '').trim();
    if (honeypot) return;

    const payload = {
      player_name: String(formData.get('player_name') || '').trim(),
      email: String(formData.get('email') || '').trim(),
      marketing_opt_in: formData.get('marketing_opt_in') === 'on',
      website: honeypot,
      ...this.pendingResult,
    };

    if (!payload.player_name || !payload.email) {
      this.formStatus.textContent = 'Name and email are required.';
      return;
    }

    this.formStatus.textContent = 'Saving your score...';

    try {
      const response = await fetch(this.submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || `Submit failed: ${response.status}`);

      this.submitted = true;
      this.saveProfile({
        name: payload.player_name,
        email: payload.email,
        bestScore: payload.score,
      });

      const rankSuffix = data.rank ? ` Current rank: #${data.rank}.` : '';
      const rewardSuffix = payload.reward_unlocked && this.rewardCode
        ? ` Reward code: ${this.rewardCode}.`
        : payload.reward_unlocked
          ? ' Reward unlocked.'
          : '';
      this.formStatus.textContent = `${data.message || 'Score saved.'}${rankSuffix}${rewardSuffix}`;
      this.setRewardUnlocked(Boolean(payload.reward_unlocked));
      await this.fetchLeaderboard();
    } catch (error) {
      console.error(error);
      this.formStatus.textContent = 'Could not save the score right now. Try again in a moment.';
    }
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  hexToRgba(hex, alpha) {
    const clean = hex.replace('#', '');
    const expanded = clean.length === 3
      ? clean.split('').map((char) => char + char).join('')
      : clean;
    const bigint = Number.parseInt(expanded, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-derby-root]').forEach((root) => {
    if (!root.__mvDerbyInstance) {
      root.__mvDerbyInstance = new MVAnimeDerby(root);
    }
  });
});